import { useState, useEffect } from "react";
import { MapPin, Plus, Pencil, X } from "lucide-react";
import { Address } from "./types";
import InputField from "@/components/form/InputField";
import { CustomButton } from "@/components/ui/Buttons";
import Modal from "@/components/ui/Modal";
import { cn } from "@/lib/utils";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import {
  useDeleteShippingAddressesMutation,
  useEditShippingAddressesMutation,
  useGetShippingAddressesQuery,
  useUploadShippingAddressesMutation,
} from "@/redux/feature/shippingAddress/shippingAddressApi";
import { useGlobalContext } from "@/context/GlobalContext";
import {
  AddNewLocation,
  TCreatorShippingData,
} from "@/pages/orderMananagement/stages/PlanningStage/ProductShipping/ProductShipping";

interface AddressSectionProps {
  // addresses: Address[];
  selectedAddressId: string | null;
  onSelectAddress: (id: string | null) => void;
  onAddAddress: (address: Omit<Address, "id">) => void;
}

const AddressSection = ({
  selectedAddressId,
  onSelectAddress,
  onAddAddress,
}: AddressSectionProps) => {
  const { userData } = useGlobalContext();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Fetch existing addresses via API
  const { data } = useGetShippingAddressesQuery(
    encodeURIComponent(userData?.email),
  );
  const locations: TCreatorShippingData[] = data?.data || [];

  // Hooks for Edit and Delete operations
  const [editShippingAddress, { isLoading: isEditing }] =
    useEditShippingAddressesMutation();
  const [deleteShippingAddress, { isLoading: isDeleting }] =
    useDeleteShippingAddressesMutation();

  const handleEditAddress = (addressId: string) => {
    // Here you can open the modal to edit the address, for example:
    const addressToEdit = locations.find((loc) => loc._id === addressId);
    if (addressToEdit) {
      // open modal and pre-populate with addressToEdit
      console.log("Edit this address", addressToEdit);
      // You can use a modal to handle address editing here
    }
  };

  const handleDeleteAddress = (addressId: string) => {
    if (confirm("Are you sure you want to delete this address?")) {
      deleteShippingAddress(addressId)
        .unwrap()
        .then(() => {
          toast.success("Address deleted successfully!");
        })
        .catch(() => {
          toast.error("Failed to delete the address.");
        });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">
          Home Address
        </span>
        <span className="text-xs text-muted-foreground">
          (Required for Shipping in videos involving Real Product)
        </span>
      </div>

      <div className="flex flex-wrap gap-3">
        {locations.map((address) => (
          <div
            key={address._id}
            onClick={() =>
              onSelectAddress(
                selectedAddressId === address._id ? null : address._id,
              )
            }
            className={cn(
              "relative select-none cursor-pointer flex-1 min-w-[200px] max-w-[280px] p-4 rounded-lg border text-left transition-all duration-200",
              selectedAddressId === address._id
                ? "border-accent bg-accent/5"
                : "border-border hover:border-primary/30",
            )}
          >
            <div className="flex items-start justify-between mb-2">
              <span
                className={cn(
                  "text-sm font-semibold uppercase",
                  selectedAddressId === address._id
                    ? "text-accent"
                    : "text-primary",
                )}
              >
                {address.location_name}
              </span>
              <div className="flex z-30 relative gap-2">
                {/* <button
                  onClick={() => handleEditAddress(address._id)}
                  disabled={isEditing}
                >
                  <Pencil className="w-4 h-4 text-accent" />
                </button> */}
                <button
                  onClick={() => handleDeleteAddress(address._id)}
                  disabled={isDeleting}
                >
                  <X className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {address.address}, {address.city}, {address.state}{" "}
              {address.pin_code}
            </p>
          </div>
        ))}

        {/* Add Address Button */}
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex-1 min-w-[140px] max-w-[160px] p-4 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-all duration-200 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <Plus className="w-5 h-5" />
          <span className="text-sm font-medium">Add Address</span>
        </button>
      </div>

      {/* Add Address Modal */}
      <Modal
        width="571px"
        padding="0px"
        setOpen={setIsAddModalOpen}
        isOpen={isAddModalOpen}
      >
        <AddNewLocation onClose={() => setIsAddModalOpen(false)} />
      </Modal>
    </div>
  );
};

export default AddressSection;
